// Pseudonymous local member identity — privacy-safe replacement for the
// old plain-email localStorage persistence. Stores only a random token and
// check-in timing metadata; never the member's email.
const STORAGE_KEY = "iwmc.nsoe.member";
const LEGACY_EMAIL_KEY = "iwmc.bls.patient_email";

function randomToken() {
  const bytes = new Uint8Array(4);
  (window.crypto || {}).getRandomValues
    ? window.crypto.getRandomValues(bytes)
    : bytes.forEach((_, i) => (bytes[i] = Math.floor(Math.random() * 256)));
  return "nsoe_" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function getMemberRecord() {
  try {
    // One-time privacy cleanup: purge the legacy plain-email key.
    localStorage.removeItem(LEGACY_EMAIL_KEY);
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const rec = JSON.parse(raw);
      if (rec && rec.memberKey) return rec;
    }
    const fresh = {
      memberKey: randomToken(),
      firstSeenAt: new Date().toISOString(),
      firstCompletedSessionAt: null,
      lastProgressCheckInAt: null,
      reminderDismissedAt: null,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
    return fresh;
  } catch (err) {
    return { memberKey: null };
  }
}

export function updateMemberRecord(patch) {
  try {
    const rec = { ...getMemberRecord(), ...patch };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rec));
    return rec;
  } catch (err) {
    return null;
  }
}

export function getMemberKey() {
  return getMemberRecord().memberKey || null;
}

export function markSessionCompleted() {
  const rec = getMemberRecord();
  if (!rec.firstCompletedSessionAt) {
    updateMemberRecord({ firstCompletedSessionAt: new Date().toISOString() });
  }
}

export function markProgressCheckIn() {
  updateMemberRecord({ lastProgressCheckInAt: new Date().toISOString() });
}

export function snoozeReminder() {
  updateMemberRecord({ reminderDismissedAt: new Date().toISOString() });
}

const DAY_MS = 24 * 60 * 60 * 1000;
export const CHECK_IN_INTERVAL_DAYS = 7;
export const SNOOZE_HOURS = 24;

// Show only to returning members: anchor = last progress check-in, falling
// back to the first completed session (never mere site visits).
export function shouldShowProgressReminder(now = Date.now()) {
  const rec = getMemberRecord();
  if (!rec.memberKey) return false;
  const anchor = rec.lastProgressCheckInAt || rec.firstCompletedSessionAt;
  if (!anchor) return false;
  if (now - Date.parse(anchor) < CHECK_IN_INTERVAL_DAYS * DAY_MS) return false;
  if (
    rec.reminderDismissedAt &&
    now - Date.parse(rec.reminderDismissedAt) < SNOOZE_HOURS * 60 * 60 * 1000
  )
    return false;
  return true;
}
